/**
 * PEStart Wizard Schritt 1 — Gründungstyp & Förderungen.
 * Totpunkt + Unternehmerlohn: read-only aus Calc / Privatkostenkompass.
 */

import React, { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useAppStore from '../../store/appStore';
import { useCalculationsSelector } from '../../store/selectors/calculations';
import usePEStartStore, {
    type FoundingType,
    type Subsidy,
    type SubsidyType,
} from '../../store/usePEStartStore';
import { resolveUnternehmerBedarf } from './peStartCalcAdapter';
import { computePEStartAufbauPlan } from './peStartPlanungsBridge';
import { PEStartPlanungsrechnerPicker } from './PEStartAufbauPlan';

const INPUT_CLASS =
    'w-full rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2.5 text-base text-pe-text dark:text-white outline-none focus:border-[#E6C299]/50';

const NUMBER_INPUT_CLASS = `${INPUT_CLASS} appearance-none [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`;

const LABEL_CLASS = 'mb-1.5 block text-xs font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400';

const SUBSIDY_TYPE_OPTIONS: { value: SubsidyType; label: string }[] = [
    { value: 'ARBEITSAGENTUR', label: 'Arbeitsagentur' },
    { value: 'PRAEMIE', label: 'Prämie' },
    { value: 'DARLEHEN', label: 'Darlehen' },
    { value: 'SONSTIGE', label: 'Sonstige' },
];

const FOUNDING_OPTIONS: { value: FoundingType; title: string; hint: string }[] = [
    {
        value: 'NEW',
        title: 'Neugründung',
        hint: 'Vollständige Erstausstattung und Aufbau von Grund auf.',
    },
    {
        value: 'TAKEOVER',
        title: 'Übernahme',
        hint: 'Kaufpreis, Übernahmekosten und Auffrischung vorhandenen Inventars.',
    },
];

function parseNonNegative(raw: string): number {
    const cleaned = String(raw ?? '')
        .replace(/\s/g, '')
        .replace(',', '.');
    if (cleaned === '' || cleaned === '-') return 0;
    const n = Number(cleaned);
    if (!Number.isFinite(n) || n < 0) return 0;
    return n;
}

function clampMonths(raw: string | number): number {
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    if (!Number.isFinite(n)) return 1;
    return Math.min(6, Math.max(1, Math.round(n)));
}

function numberFieldValue(n: number): string | number {
    if (!Number.isFinite(n) || n === 0) return '';
    return n;
}

function selectInputOnFocus(e: React.FocusEvent<HTMLInputElement>) {
    e.target.select();
}

function subsidyFieldErrors(s: Subsidy): { label?: string; amount?: string; monthsCovered?: string } {
    const errors: { label?: string; amount?: string; monthsCovered?: string } = {};
    if (!String(s.label ?? '').trim()) errors.label = 'Bezeichnung erforderlich';
    if (!(typeof s.amount === 'number') || !Number.isFinite(s.amount) || s.amount < 0) {
        errors.amount = 'Betrag muss ≥ 0 sein';
    }
    if (
        !(typeof s.monthsCovered === 'number') ||
        !Number.isFinite(s.monthsCovered) ||
        s.monthsCovered < 1 ||
        s.monthsCovered > 6
    ) {
        errors.monthsCovered = 'Laufzeit 1–6 Monate';
    }
    return errors;
}

export type PEStartWizardStep1Props = {
    className?: string;
};

const PEStartWizardStep1: React.FC<PEStartWizardStep1Props> = ({ className }) => {
    const calc = useCalculationsSelector() as any;
    const privateCostData = useAppStore((s) => s.clientData?.privateCostData);
    const unternehmerData = useAppStore((s) => s.clientData?.unternehmerData);
    const clientData = useAppStore((s) => s.clientData);
    const settings = useAppStore((s) => s.settings);
    const setClientData = useAppStore((s) => s.setClientData);
    const appSlice = useAppStore(
        useShallow((s) => ({
            planungsAnalysePrognose: s.planungsAnalysePrognose,
            planungsAnalyseArchiv: s.planungsAnalyseArchiv,
            planungsAnalyseActiveSzenarioId: s.planungsAnalyseActiveSzenarioId,
            planungsAnalyseBasisDatenTyp: s.planungsAnalyseBasisDatenTyp,
            peProfitDataVersion: s.peProfitDataVersion,
            clientData: s.clientData,
        }))
    );

    const {
        foundingType,
        subsidies,
        selectedPlanungsrechnerSzenarioId,
        setFoundingType,
        setSelectedPlanungsrechnerSzenarioId,
        updateSubsidies,
        addSubsidy,
        removeSubsidy,
    } = usePEStartStore(
        useShallow((s) => ({
            foundingType: s.foundingType,
            subsidies: s.subsidies,
            selectedPlanungsrechnerSzenarioId: s.selectedPlanungsrechnerSzenarioId,
            setFoundingType: s.setFoundingType,
            setSelectedPlanungsrechnerSzenarioId: s.setSelectedPlanungsrechnerSzenarioId,
            updateSubsidies: s.updateSubsidies,
            addSubsidy: s.addSubsidy,
            removeSubsidy: s.removeSubsidy,
        }))
    );

    const aufbauModel = useMemo(
        () =>
            computePEStartAufbauPlan(
                appSlice,
                calc,
                clientData,
                settings,
                selectedPlanungsrechnerSzenarioId
            ),
        [appSlice, calc, clientData, settings, selectedPlanungsrechnerSzenarioId]
    );

    const handleSelectPlanungsrechner = (id: string | null) => {
        setSelectedPlanungsrechnerSzenarioId(id);
        if (typeof setClientData === 'function') {
            setClientData((prev: any) => ({
                ...prev,
                peStartSelectedPlanungsrechnerId: id,
            }));
        }
    };

    const pauschaleBedarf = useMemo(() => {
        const fromCalc = Number(calc?.gesamtlohn) || 0;
        if (fromCalc > 0) return fromCalc;
        return (
            (Number(unternehmerData?.entnahme) || 0) + (Number(unternehmerData?.sozialkosten) || 0)
        );
    }, [calc?.gesamtlohn, unternehmerData?.entnahme, unternehmerData?.sozialkosten]);

    const ul = useMemo(
        () => resolveUnternehmerBedarf(privateCostData, pauschaleBedarf),
        [privateCostData, pauschaleBedarf]
    );
    const totpunkt = Number(calc?.sollumsatz) || 0;
    const planumsatz = Number(calc?.planumsatzGesamtNetto) || 0;
    const currencyCode = calc?.currencyCode || 'EUR';
    const formatCurrency =
        typeof calc?.formatCurrency === 'function'
            ? calc.formatCurrency
            : (v: number, d = 0) =>
                  new Intl.NumberFormat('de-DE', {
                      style: 'currency',
                      currency: currencyCode,
                      maximumFractionDigits: d,
                      minimumFractionDigits: d,
                  }).format(v || 0);

    const subsidyErrors = useMemo(() => {
        const map: Record<string, ReturnType<typeof subsidyFieldErrors>> = {};
        for (const row of subsidies) map[row.id] = subsidyFieldErrors(row);
        return map;
    }, [subsidies]);

    const patchSubsidy = (id: string, patch: Partial<Subsidy>) => {
        updateSubsidies({ items: [{ id, ...patch }] });
    };

    return (
        <section
            className={`min-w-0 overflow-x-hidden rounded-2xl border border-pe-border dark:border-[#2a2a2a] bg-pe-card dark:bg-[#0f0f0f] p-5 text-base ${className ?? ''}`}
        >
            <header className="mb-6 border-b border-pe-border dark:border-[#222] pb-4">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500 dark:text-slate-500">
                    PEStart · Schritt 1
                </p>
                <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299]">
                    Gründungstyp & Förderungen
                </h2>
                <p className="mt-2 text-sm text-stone-500 dark:text-slate-500">
                    Stammdaten kommen live aus Kostenstruktur und Privatkostenkompass — hier
                    Gründungstyp und Förderungen. Kreditplanung folgt in Schritt 2 nach den
                    Investitionen.
                </p>
            </header>

            {/* Read-only Referenz */}
            <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 dark:text-slate-500">
                        Totpunkt (Sollumsatz)
                    </p>
                    <p className="mt-2 text-lg font-black tabular-nums text-pe-accent-text dark:text-[#E6C299]">
                        {formatCurrency(totpunkt, 0)}
                    </p>
                    <p className="mt-1 text-xs text-stone-500 dark:text-slate-500">
                        Aus Programm-Kalkulation übernommen (schreibgeschützt)
                    </p>
                </div>
                <div className="rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 dark:text-slate-500">
                        Unternehmerlohn / Privatbedarf
                    </p>
                    <p className="mt-2 text-lg font-black tabular-nums text-pe-text dark:text-white">
                        {formatCurrency(ul.bedarf, 0)}
                    </p>
                    <p className="mt-1 text-xs text-stone-500 dark:text-slate-500">
                        {ul.source === 'kompass'
                            ? 'Aus Privatkostenkompass übernommen'
                            : 'Kompass leer — Pauschale aus Unternehmerwerte (Entnahme + Sozialkosten)'}
                        {ul.bedarfRounded > 0 && (
                            <> · gerundet {formatCurrency(ul.bedarfRounded, 0)}</>
                        )}
                    </p>
                </div>
                <div className="rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 dark:text-slate-500">
                        Planumsatz netto
                    </p>
                    <p className="mt-2 text-lg font-black tabular-nums text-pe-text dark:text-white">
                        {formatCurrency(planumsatz, 0)}
                    </p>
                    <p className="mt-1 text-xs text-stone-500 dark:text-slate-500">
                        Aus Lohnfaktor / Programm-Kalkulation
                    </p>
                </div>
            </div>

            {/* Gründungstyp */}
            <div className="mb-6">
                <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                    Gründungstyp
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {FOUNDING_OPTIONS.map((opt) => {
                        const active = foundingType === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => setFoundingType(opt.value)}
                                className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                                    active
                                        ? 'border-[#E6C299] bg-[#E6C299]/15 text-pe-accent-text dark:text-[#E6C299]'
                                        : 'border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#141414] text-pe-muted dark:text-slate-400 hover:border-pe-border-strong dark:hover:border-[#444] hover:text-pe-text dark:hover:text-white'
                                }`}
                            >
                                <span className="block text-sm font-bold uppercase tracking-wider">
                                    {opt.title}
                                </span>
                                <span
                                    className={`mt-1 block text-xs ${active ? 'text-pe-accent-text dark:text-[#E6C299]/80' : 'text-stone-500 dark:text-slate-500'}`}
                                >
                                    {opt.hint}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Aufbauplan-Auswahl aus Planungsrechner (Coach) */}
            <div className="mb-6">
                <PEStartPlanungsrechnerPicker
                    model={aufbauModel}
                    selectedId={selectedPlanungsrechnerSzenarioId}
                    onSelect={handleSelectPlanungsrechner}
                    compact
                />
            </div>

            {/* Förderungen */}
            <div>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <p className="text-sm font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                            Förderungen & Zuschüsse
                        </p>
                        <p className="mt-1 text-sm text-stone-500 dark:text-slate-500">
                            Reduzieren den Finanzbedarf in den ersten 1–6 Monaten.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() =>
                            addSubsidy({
                                type: 'ARBEITSAGENTUR',
                                label: '',
                                amount: 0,
                                monthsCovered: 6,
                                notes: '',
                            })
                        }
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/15 px-3 py-2.5 text-sm font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/25"
                    >
                        + Förderung
                    </button>
                </div>

                {subsidies.length === 0 ? (
                    <p className="rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-4 py-3 text-sm text-stone-500 dark:text-slate-500">
                        Noch keine Förderungen erfasst.
                    </p>
                ) : (
                    <ul className="space-y-3">
                        {subsidies.map((row) => {
                            const errs = subsidyErrors[row.id] || {};
                            return (
                                <li
                                    key={row.id}
                                    className="space-y-3 rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-3"
                                >
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        <div>
                                            <label className={LABEL_CLASS}>Typ</label>
                                            <select
                                                value={row.type}
                                                onChange={(e) =>
                                                    patchSubsidy(row.id, {
                                                        type: e.target.value as SubsidyType,
                                                    })
                                                }
                                                className={INPUT_CLASS}
                                            >
                                                {SUBSIDY_TYPE_OPTIONS.map((o) => (
                                                    <option key={o.value} value={o.value}>
                                                        {o.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Bezeichnung</label>
                                            <input
                                                type="text"
                                                value={row.label ?? ''}
                                                onChange={(e) =>
                                                    patchSubsidy(row.id, { label: e.target.value })
                                                }
                                                placeholder="z. B. Gründungszuschuss"
                                                className={INPUT_CLASS}
                                            />
                                            {errs.label && (
                                                <p className="mt-1 text-xs font-medium text-red-400">
                                                    {errs.label}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Betrag (EUR)</label>
                                            <input
                                                type="number"
                                                min={0}
                                                step={100}
                                                inputMode="decimal"
                                                placeholder="0"
                                                value={numberFieldValue(row.amount)}
                                                onChange={(e) =>
                                                    patchSubsidy(row.id, {
                                                        amount: parseNonNegative(e.target.value),
                                                    })
                                                }
                                                onFocus={selectInputOnFocus}
                                                className={NUMBER_INPUT_CLASS}
                                            />
                                            {errs.amount && (
                                                <p className="mt-1 text-xs font-medium text-red-400">
                                                    {errs.amount}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>
                                                Laufzeit (Monate, 1–6)
                                            </label>
                                            <input
                                                type="number"
                                                min={1}
                                                max={6}
                                                step={1}
                                                inputMode="numeric"
                                                value={
                                                    Number.isFinite(row.monthsCovered)
                                                        ? row.monthsCovered
                                                        : 1
                                                }
                                                onChange={(e) =>
                                                    patchSubsidy(row.id, {
                                                        monthsCovered: clampMonths(e.target.value),
                                                    })
                                                }
                                                onFocus={selectInputOnFocus}
                                                className={NUMBER_INPUT_CLASS}
                                            />
                                            {errs.monthsCovered && (
                                                <p className="mt-1 text-xs font-medium text-red-400">
                                                    {errs.monthsCovered}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>Notizen</label>
                                        <input
                                            type="text"
                                            value={row.notes ?? ''}
                                            onChange={(e) =>
                                                patchSubsidy(row.id, { notes: e.target.value })
                                            }
                                            placeholder="Optional"
                                            className={INPUT_CLASS}
                                        />
                                    </div>
                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={() => removeSubsidy(row.id)}
                                            className="text-sm font-medium text-red-400/90 underline-offset-2 hover:text-red-300 hover:underline"
                                        >
                                            Entfernen
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </section>
    );
};

export default PEStartWizardStep1;
